/**
 * useSyncStatus — React hook for PowerSync connection state
 *
 * Reads the connection state directly from the renderer-side PowerSync
 * database instance (no IPC polling needed). Hydrates immediately from
 * `db.currentStatus` and updates in real-time via `db.registerListener`.
 */

import { useEffect, useState } from 'react';

import { db } from '@/renderer/lib/powersync/database';

import type {
  PowerSyncStatusData,
  RawPowerSyncStatus,
} from '@/renderer/types/powersync';
import { extractSyncErrorMessage } from '@/renderer/types/powersync';

const DEFAULT_STATUS: PowerSyncStatusData = {
  connected: false,
  connecting: false,
  status: 'initial',
  lastSyncedAt: null,
  hasSynced: false,
  uploadQueueSize: 0,
  downloading: false,
  downloadError: null,
  uploadError: null,
};

function mapStatus(s: RawPowerSyncStatus): PowerSyncStatusData {
  return {
    connected: s.connected,
    connecting: s.connecting ?? false,
    status: s.connected
      ? 'connected'
      : (s.connecting ?? false)
        ? 'connecting'
        : 'disconnected',
    lastSyncedAt: s.lastSyncedAt?.toISOString() ?? null,
    hasSynced: s.hasSynced ?? false,
    uploadQueueSize: s.dataFlowStatus?.uploading ? 1 : 0,
    downloading: s.dataFlowStatus?.downloading ?? false,
    downloadError: extractSyncErrorMessage(s.dataFlowStatus?.downloadError),
    uploadError: extractSyncErrorMessage(s.dataFlowStatus?.uploadError),
  };
}

export function useSyncStatus(): PowerSyncStatusData {
  const [syncStatus, setSyncStatus] = useState<PowerSyncStatusData>(() => {
    try {
      return mapStatus(db.currentStatus);
    } catch {
      return DEFAULT_STATUS;
    }
  });

  useEffect(() => {
    // Hydrate with the latest snapshot in case it changed before mount.
    try {
      setSyncStatus(mapStatus(db.currentStatus));
    } catch {
      // db not ready yet — stay on DEFAULT_STATUS
    }

    // Subscribe to live status changes.
    const cleanup = db.registerListener({
      statusChanged(s) {
        setSyncStatus(mapStatus(s));
      },
    });

    return cleanup;
  }, []);

  return syncStatus;
}
